import formatMessage from '@ui5/webcomponents-base/dist/util/formatMessage';
import { useI18nBundle } from '@ui5/webcomponents-react-base';
import { TranslationBundle } from '../i18n';
import * as appEditorAngular from '../translations/app-editor.angular.json';
import * as appEditorAuthentication from '../translations/app-editor.authentication.json';
import * as appEditorCanvas from '../translations/app-editor.canvas.json';
import * as appEditorCanvasContextMenu from '../translations/app-editor.canvas-context-menu.json';
import * as appEditorHeader from '../translations/app-editor.header.json';
import * as appEditorPublish from '../translations/app-editor.publish.json';
import * as appEditorHelp from '../translations/app-editor.help.json';
import * as appEditorIntegrations from '../translations/app-editor.integrations.json';
import * as appEditorMarketplaceDetail from '../translations/app-editor.marketplace-detail.json';
import * as appEditorMarketplaceSearch from '../translations/app-editor.marketplace-search.json';
import * as appEditorNavigation from '../translations/app-editor.navigation.json';
import * as appEditorTheme from '../translations/app-editor.theme.json';
import * as appEditorVariableProperties from '../translations/app-editor.variable-properties.json';
import * as appEditorVariables from '../translations/app-editor.variables.json';
import * as appEditorTemplateEditor from '../translations/app-editor.template-editor.json';
import * as buildUi from '../translations/build-ui.json';
import * as appBuildPage from '../translations/app-build.page.json';
import * as appBuildHeader from '../translations/app-build.header.json';
import * as appBuildHistory from '../translations/app-build.history.json';
import * as commonSignedOut from '../translations/common.signed-out.json';
import { TranslationKey } from '../types';

const fallbackMap: Record<TranslationBundle, Record<string, string>> = {
  'app-editor.canvas': appEditorCanvas,
  'app-editor.canvas-context-menu': appEditorCanvasContextMenu,
  'app-editor.header': appEditorHeader,
  'app-editor.publish': appEditorPublish,
  'app-editor.marketplace-search': appEditorMarketplaceSearch,
  'app-editor.marketplace-detail': appEditorMarketplaceDetail,
  'app-editor.authentication': appEditorAuthentication,
  'app-editor.help': appEditorHelp,
  'app-editor.integrations': appEditorIntegrations,
  'app-editor.theme': appEditorTheme,
  'app-editor.navigation': appEditorNavigation,
  'app-editor.variables': appEditorVariables,
  'app-editor.variable-properties': appEditorVariableProperties,
  'app-editor.template-editor': appEditorTemplateEditor,
  'build-ui': buildUi,
  'app-build.page': appBuildPage,
  'app-build.header': appBuildHeader,
  'app-build.history': appBuildHistory,
  'common.signed-out': commonSignedOut,
  'app-editor.angular': appEditorAngular,
};

export type I18nText<T> = {
  key: TranslationKey<T>;
  defaultText: string;
};

export default function useTranslation<T extends TranslationBundle>(bundleName: T) {
  const i18nBundle = useI18nBundle(bundleName);

  // Fallback to the default language if the correct bundle is not loaded yet
  if (i18nBundle.packageName !== bundleName) {
    return {
      t: (text: TranslationKey<T> | I18nText<T>, ...params: Array<number | string>) =>
        typeof text === 'string' ? formatMessage(fallbackMap[bundleName][text], params) : text.defaultText,
    };
  }

  return {
    t(text: TranslationKey<T> | I18nText<T>, ...params: Array<number | string>) {
      const translation = i18nBundle.getText(text, ...params);
      // Fallback to the default language if the translation is missing and there is no default text
      if (translation === text) {
        return formatMessage(fallbackMap[bundleName][text], params);
      }
      return translation;
    },
  };
}
